import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import {
  Home, ArrowLeftRight, PieChart, Plus, Grid, Wallet, Tag,
  Bell, Target, Sparkles, User, Shield, ChevronRight, CreditCard, Lock
} from 'lucide-react';
import { APP_VERSION } from '@/config/version';

const MobileBottomNav = ({ onAddClick }) => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isChatbotHidden, setIsChatbotHidden] = useState(false);

  // Gestos táctiles para arrastrar y cerrar el bottom sheet
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const [dragY, setDragY] = useState(0);

  useEffect(() => {
    // Verificar si el chatbot está oculto
    const checkChatbotStatus = () => {
      const hidden = localStorage.getItem('chatbot_hidden') === 'true';
      setIsChatbotHidden(hidden);
    };

    checkChatbotStatus();
    window.addEventListener('storage', checkChatbotStatus);
    window.addEventListener('chatbot-hidden-changed', checkChatbotStatus);

    return () => {
      window.removeEventListener('storage', checkChatbotStatus);
      window.removeEventListener('chatbot-hidden-changed', checkChatbotStatus);
    };
  }, []);

  // Bloqueo estricto de scroll de body y html cuando la hoja está abierta
  useEffect(() => {
    if (showMoreMenu) {
      const prevBodyOverflow = document.body.style.overflow;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyTouchAction = document.body.style.touchAction;

      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';

      return () => {
        document.body.style.overflow = prevBodyOverflow;
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.touchAction = prevBodyTouchAction;
      };
    }
  }, [showMoreMenu]);

  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowMoreMenu(false);
      setIsClosing(false);
      setDragY(0);
      startY.current = 0;
      isDragging.current = false;
    }, 250);
  };

  const isFromHandle = useRef(false);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    const isAtTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    const isAtTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    if (diff > 0 && (isFromHandle.current || isAtTop)) {
      setDragY(diff);
    } else if (diff < 0) {
      isDragging.current = false;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragY > threshold) {
      triggerHaptic(15);
      closeMenu();
    } else {
      setDragY(0);
    }
    startY.current = 0;
    isFromHandle.current = false;
  };

  const triggerHaptic = (duration = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(duration);
    }
  };

  const mainNavItems = [
    { path: '/dashboard', label: 'Inicio', icon: Home },
    { path: '/transactions', label: 'Movimientos', icon: ArrowLeftRight },
    { isAction: true },
    { path: '/statistics', label: 'Estadísticas', icon: PieChart },
    { isMore: true, label: 'Herramientas', icon: Grid }
  ];

  const isProUser = user?.subscription_tier === 'pro' || user?.role === 'admin';
  const isPremiumOrProUser = user?.subscription_tier === 'premium' || isProUser;

  const menuGroups = [
    {
      title: 'Gestión',
      items: [
        { path: '/accounts', label: 'Cuentas Bancarias', desc: 'Saldos, tarjetas y cuentas', icon: Wallet, iconBg: 'bg-blue-500' },
        { path: '/categories', label: 'Categorías', desc: 'Personaliza ingresos y gastos', icon: Tag, iconBg: 'bg-emerald-500' },
        { path: '/debts', label: 'Control de Deudas', desc: 'Deudas, abonos y pagos', icon: CreditCard, iconBg: 'bg-rose-500' },
      ]
    },
    {
      title: 'Finanzas & IA',
      items: [
        { path: '/payment-reminders', label: 'Recordatorios', desc: 'Notificaciones de facturas', icon: Bell, iconBg: 'bg-amber-500' },
        { 
          path: '/goals', 
          label: 'Objetivos de Ahorro', 
          desc: 'Metas y presupuestos', 
          icon: Target, 
          iconBg: 'bg-indigo-500',
          ...(!isPremiumOrProUser ? { badge: 'Premium', hasLock: true } : {})
        },
        { 
          path: '/advanced-analytics', 
          label: 'Análisis Avanzados', 
          desc: 'Reportes detallados con IA', 
          icon: Sparkles, 
          iconBg: 'bg-purple-500', 
          badge: 'Pro',
          hasLock: !isProUser
        },
      ]
    },
    {
      title: 'Cuenta',
      items: [
        { path: '/profile', label: 'Mi Perfil & Ajustes', desc: 'Cuenta y suscripción', icon: User, iconBg: 'bg-slate-600' },
        ...(user?.role === 'admin' ? [{ path: '/admin', label: 'Panel Admin', desc: 'Gestión completa del sistema', icon: Shield, iconBg: 'bg-rose-600', badge: 'Admin' }] : [])
      ]
    }
  ];

  return (
    <>
      {/* Navegación Inferior Móvil Fija */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200/80 pb-safe-area-inset-bottom pt-1 z-40 shadow-[0_-4px_25px_rgba(0,0,0,0.06)] w-full">
        <div className="flex justify-between items-center w-full max-w-md mx-auto h-14 px-1">
          {mainNavItems.map((item, index) => {
            if (item.isAction) {
              return (
                <button
                  key={index}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    triggerHaptic(20);
                    if (onAddClick) onAddClick();
                  }}
                  className="relative -top-4 w-13 h-13 min-w-[52px] min-h-[52px] shrink-0 aspect-square mx-1 bg-gradient-to-tr from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 hover:scale-105 active:scale-95 transition-transform"
                >
                  <Plus className="w-6.5 h-6.5 stroke-[2.5px]" />
                </button>
              );
            }

            if (item.isMore) {
              const isMoreActive = showMoreMenu || ['/accounts', '/categories', '/debts', '/payment-reminders', '/goals', '/advanced-analytics', '/profile', '/admin'].includes(location.pathname);
              return (
                <button
                  key={index}
                  onClick={() => {
                    triggerHaptic(10);
                    setShowMoreMenu(true);
                  }}
                  className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1 transition-colors ${isMoreActive ? 'text-blue-600 font-semibold' : 'text-slate-400'
                    }`}
                >
                  <Grid className={`w-5.5 h-5.5 shrink-0 ${isMoreActive ? 'stroke-[2.5px]' : 'stroke-[1.5px]'}`} />
                  <span className="text-[10px] font-medium leading-tight truncate max-w-full px-0.5">{item.label}</span>
                </button>
              );
            }

            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => triggerHaptic(8)}
                className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1 transition-colors ${isActive ? 'text-blue-600 font-semibold' : 'text-slate-400'
                  }`}
              >
                <item.icon className={`w-5.5 h-5.5 shrink-0 ${isActive ? 'stroke-[2.5px]' : 'stroke-[1.5px]'}`} />
                <span className="text-[10px] font-medium leading-tight truncate max-w-full px-0.5">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Menú Deslizable "Más" (Bottom Sheet - Estilo Lista iOS / Fintech) */}
      {showMoreMenu && (
        <div 
          className={`fixed inset-0 z-50 flex flex-col justify-end bg-slate-950/60 backdrop-blur-md transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100 animate-fade-in'}`}
          style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
          {/* Overlay touch para cerrar */}
          <div
            className="flex-1"
            style={{ touchAction: 'none' }}
            onClick={closeMenu}
          />

          {/* Hoja Inferior */}
          <div
            ref={sheetRef}
            onTouchStart={(e) => handleTouchStart(e, false)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              transform: isClosing ? 'translateY(100%)' : (dragY > 0 ? `translate3d(0, ${dragY}px, 0)` : 'translateY(0)'),
              transition: isDragging.current ? 'none' : 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain'
            }}
            className="bg-slate-100 rounded-t-[2.5rem] p-5 shadow-2xl max-h-[85vh] overflow-y-auto no-scrollbar border-t border-white/40 will-change-transform overscroll-contain"
          >

            {/* Zona táctil de Arrastre Superior */}
            <div
              onTouchStart={(e) => handleTouchStart(e, true)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="pt-1 pb-2 select-none cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto mb-3 hover:bg-slate-400 transition-colors" />

              <div className="pb-2">
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Herramientas</h3>
                <p className="text-xs text-slate-500 font-medium">Accesos rápidos y funciones avanzadas</p>
              </div>
            </div>

            {/* Grupos de lista */}
            <div className="space-y-4 pb-4">
              {menuGroups.map((group, groupIdx) => (
                <div key={groupIdx}>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 px-1">{group.title}</p>
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                    {group.items.map((menu, itemIdx) => {
                      const MenuIcon = menu.icon;
                      const isActive = location.pathname === menu.path;

                      return (
                        <Link
                          key={menu.path}
                          to={menu.path}
                          onClick={closeMenu}
                          className={`w-full flex items-center justify-between px-4 py-3.5 active:bg-slate-50 transition-colors ${itemIdx < group.items.length - 1 ? 'border-b border-slate-100' : ''
                            } ${isActive ? 'bg-blue-50/60' : ''}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 ${menu.iconBg} rounded-xl flex items-center justify-center shrink-0 shadow-sm`}>
                              <MenuIcon className="w-4.5 h-4.5 text-white" style={{ width: '18px', height: '18px' }} />
                            </div>
                            <div className="text-left min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-semibold truncate ${isActive ? 'text-blue-600' : 'text-slate-800'}`}>
                                  {menu.label}
                                </p>
                                {menu.badge && (
                                  <span className={`px-2 py-0.5 text-[9px] font-extrabold rounded-full uppercase tracking-wider shrink-0 flex items-center gap-1 ${
                                    menu.badge === 'Pro'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {menu.hasLock && <Lock className="w-2.5 h-2.5 shrink-0" />}
                                    {menu.badge}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 truncate">{menu.desc}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 ml-2" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Botón especial para reactivar la burbuja del Chatbot IA */}
              {isChatbotHidden && (
                <button
                  onClick={() => {
                    window.dispatchEvent(new Event('reactivate-chatbot'));
                    closeMenu();
                  }}
                  className="w-full flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md active:scale-98 transition-all mt-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-bold text-sm leading-tight">Activar Burbuja IA</h4>
                      <p className="text-xs text-blue-100 leading-tight">Restaurar chatbot en pantalla</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-white/20 text-xs font-bold">Activar</span>
                </button>
              )}
            </div>

            {/* Versionado dinámico */}
            <div className="pt-3 pb-2 text-center border-t border-slate-200/60">
              <p className="text-[11px] font-bold text-slate-400 tracking-wider">PGastos v{APP_VERSION}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav;
